import { z } from "zod";

import { pinned } from "./pin";

/**
 * Comic-category vocabulary + typed draft — single source of truth for the
 * wire AND the UI enums. The DB CHECKs on `listing_comic_details`
 * (migration 20260802194900_add_comics_category.sql) mirror these tuples;
 * extending one side without the other fails either the boundary parse or
 * the insert — never silently.
 *
 * DELIBERATE UPGRADE over the card path: `cardDraft` is an opaque
 * `z.record(z.unknown())` with ~400 lines of inline route validation
 * (SELL_FLOW_AUDIT_2026_08_02 §2.3.2). The comic draft parses ONCE here —
 * after `safeParse`, every consumer holds a typed `ComicDraft` and the
 * route only does IO-bound checks (taxonomy id existence).
 */

export const COMIC_FORMATS = [
  "single_issue",
  "tpb",
  "hardcover",
  "omnibus",
  "graphic_novel",
  "box_set",
] as const;
export type ComicFormat = (typeof COMIC_FORMATS)[number];

export const COMIC_RAW_CONDITIONS = [
  "near_mint",
  "very_fine",
  "fine",
  "very_good",
  "good",
  "fair",
  "poor",
] as const;
export type ComicRawCondition = (typeof COMIC_RAW_CONDITIONS)[number];

export const COMIC_GRADE_COMPANIES = [
  "cgc",
  "cbcs",
  "pgx",
  "egs",
  "other",
] as const;
export type ComicGradeCompany = (typeof COMIC_GRADE_COMPANIES)[number];

export const COMIC_PAGE_QUALITIES = [
  "white",
  "off_white_to_white",
  "off_white",
  "cream_to_off_white",
  "cream",
  "tan",
  "brittle",
] as const;
export type ComicPageQuality = (typeof COMIC_PAGE_QUALITIES)[number];

/**
 * Graded/raw coherence — same invariants as the three DB CHECKs
 * (`chk_comic_graded_complete`, `chk_comic_graded_no_raw`,
 * `chk_comic_raw_no_slab_fields`), enforced at the boundary so a violating
 * payload 400s with a field-addressed message instead of a 500 on insert.
 */
function refineGradedCoherence(
  draft: {
    isGraded?: boolean;
    gradeCompany?: ComicGradeCompany | null;
    gradeValue?: number | null;
    certNumber?: string | null;
    rawCondition?: ComicRawCondition | null;
  },
  ctx: z.RefinementCtx,
) {
  if (draft.isGraded) {
    if (!draft.gradeCompany || draft.gradeValue == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["gradeCompany"],
        message: "Grading company and grade value are required when isGraded",
      });
    }
    if (draft.rawCondition != null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["rawCondition"],
        message: "A graded comic cannot also carry a raw condition",
      });
    }
  } else if (
    draft.gradeCompany != null ||
    draft.gradeValue != null ||
    (draft.certNumber != null && draft.certNumber !== "")
  ) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["isGraded"],
      message: "Slab fields (company/value/cert) require isGraded",
    });
  }
}

/**
 * The interface is the source of truth; `ComicDraftSchema` is pinned to it.
 * Inferring the type FROM the schema (the shape this file shipped with)
 * inverts that: any accidental change to a zod leaf silently redefines the
 * type every consumer holds, which is exactly the drift `pinned<T>()` exists
 * to make a compile error (WEB_MOBILE_CONTRACT §8 G — why the interface stays
 * the SoT).
 *
 * Every field is optional-and-nullable because a draft is a partially filled
 * form: `.nullish()` accepts an explicitly-cleared field (`null`) as well as
 * an untouched one, and the graded/raw coherence rules are enforced by
 * `refineGradedCoherence`, not by requiredness.
 */
export interface ComicDraft {
  publisherId?: string | null;
  publisherCustomText?: string | null;
  seriesId?: string | null;
  seriesCustomText?: string | null;

  format?: ComicFormat | null;
  issueNumber?: string | null;
  issueTitle?: string | null;
  publicationYear?: number | null;
  coverVariant?: string | null;
  isKeyIssue?: boolean;
  keyNotes?: string | null;

  rawCondition?: ComicRawCondition | null;
  isGraded?: boolean;
  gradeCompany?: ComicGradeCompany | null;
  gradeValue?: number | null;
  certNumber?: string | null;
  isSignatureSeries?: boolean;
  signatureDetails?: string | null;
  pageQuality?: ComicPageQuality | null;

  notes?: string | null;
}

export const ComicDraftSchema = pinned<ComicDraft>()(
  z
    .object({
      publisherId: z.string().uuid().nullish(),
      publisherCustomText: z.string().max(120).nullish(),
      seriesId: z.string().uuid().nullish(),
      seriesCustomText: z.string().max(200).nullish(),

      format: z.enum(COMIC_FORMATS).nullish(),
      issueNumber: z.string().max(40).nullish(),
      issueTitle: z.string().max(200).nullish(),
      publicationYear: z.number().int().min(1900).max(2100).nullish(),
      coverVariant: z.string().max(200).nullish(),
      isKeyIssue: z.boolean().optional(),
      keyNotes: z.string().max(500).nullish(),

      rawCondition: z.enum(COMIC_RAW_CONDITIONS).nullish(),
      isGraded: z.boolean().optional(),
      gradeCompany: z.enum(COMIC_GRADE_COMPANIES).nullish(),
      gradeValue: z.number().min(0.5).max(10).nullish(),
      certNumber: z.string().max(60).nullish(),
      isSignatureSeries: z.boolean().optional(),
      signatureDetails: z.string().max(200).nullish(),
      pageQuality: z.enum(COMIC_PAGE_QUALITIES).nullish(),

      notes: z.string().max(500).nullish(),
    })
    .superRefine(refineGradedCoherence),
);
